import api, { buildQueryParams, normalizeList } from './api';

// Units of Measure
export const getUnits = async () => {
    const response = await api.get('/api/inventory/units/');
    return response.data;
};

export const getUnit = async (id) => {
    const response = await api.get(`/api/inventory/units/${id}/`);
    return response.data;
};

export const createUnit = async (data) => {
    const response = await api.post('/api/inventory/units/', data);
    return response.data;
};

export const updateUnit = async (id, data) => {
    const response = await api.put(`/api/inventory/units/${id}/`, data);
    return response.data;
};

export const deleteUnit = async (id) => {
    await api.delete(`/api/inventory/units/${id}/`);
};

// Equipment
export const getEquipment = async (params = {}) => {
    try {
        // Use inventory endpoint for better data including status
        let url = '/api/inventory/equipment/';
        
        // Build query parameters including pagination
        const queryString = buildQueryParams(params);
        if (queryString) {
            url += `?${queryString}`;
        }
        
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error in getEquipment:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const getEquipmentItem = async (id) => {
    try {
        const response = await api.get(`/api/master-data/equipment/${id}/`);
        return response.data;
    } catch (error) {
        console.error('Error in getEquipmentItem:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const createEquipment = async (data) => {
    try {
        const response = await api.post('/api/master-data/equipment/', data);
        return response.data;
    } catch (error) {
        console.error('Error in createEquipment:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const updateEquipment = async (id, data) => {
    try {
        const response = await api.put(`/api/master-data/equipment/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error('Error in updateEquipment:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const deleteEquipment = async (id) => {
    try {
        await api.delete(`/api/master-data/equipment/${id}/`);
        return true;
    } catch (error) {
        console.error('Error in deleteEquipment:', error);
        throw error;
    }
};

// Equipment Actions
export const updateEquipmentStatus = async (id, status) => {
    const response = await api.post(`/api/master-data/equipment/${id}/update_status/`, { status });
    return response.data;
};

// Returns consumables from the dedicated low-stock endpoint (paginated, server-filtered).
// Use this instead of client-side filtering on page-1 results.
export const getLowStockConsumables = async (params = {}) => {
    try {
        const response = await api.get('/api/inventory/low-stock/', { params });
        // Return the full paginated response so callers can read count/total_pages
        return response.data;
    } catch (error) {
        console.error('Error in getLowStockConsumables:', error);
        return { results: [], count: 0, total_pages: 1, has_next: false, has_previous: false };
    }
};

// Consumables
// Paginated version for list screens (returns full DRF page structure).
export const getConsumablesPage = async (params = {}) => {
    try {
        let url = '/api/inventory/consumables/';
        
        // Build query parameters including pagination
        const queryString = buildQueryParams(params);
        if (queryString) {
            url += `?${queryString}`;
        }
        
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error in getConsumablesPage:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

// Backwards-compatible helper that returns just a flat array of consumables,
// used by places that don't need pagination (dropdowns, dashboards, etc.).
export const getConsumables = async (params = {}) => {
    const page = await getConsumablesPage(params);
    return normalizeList(page);
};

/** For paginated lists (e.g. Inventory, Dashboard counts): returns full API response { results, count, ... }. */
export const getConsumablesPaginated = async (params = {}) => {
    const url = '/api/inventory/consumables/';
    const queryString = buildQueryParams(params);
    const response = await api.get(queryString ? `${url}?${queryString}` : url);
    return response.data;
};

export const getFilmConsumables = async () => {
    try {
        const response = await api.get('/api/inventory/consumables/film-consumables/');
        return response.data;
    } catch (error) {
        console.error('Error in getFilmConsumables:', error);
        return [];
    }
};

// Returns {total, low, reorder, expired} counts for the stock filter tabs.
export const getConsumablesSummary = async () => {
    try {
        const response = await api.get('/api/inventory/consumables/summary/');
        return response.data;
    } catch (error) {
        console.error('Error in getConsumablesSummary:', error);
        return { total: 0, low: 0, reorder: 0, expired: 0 };
    }
};

export const getConsumable = async (id) => {
    try {
        const response = await api.get(`/api/master-data/consumables/${id}/`);
        return response.data;
    } catch (error) {
        console.error('Error in getConsumable:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const createConsumable = async (data) => {
    try {
        const response = await api.post('/api/master-data/consumables/', data);
        return response.data;
    } catch (error) {
        console.error('Error in createConsumable:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const updateConsumable = async (id, data) => {
    try {
        const response = await api.put(`/api/inventory/consumables/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error('Error in updateConsumable:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const deleteConsumable = async (id) => {
    try {
        await api.delete(`/api/inventory/consumables/${id}/`);
        return true;
    } catch (error) {
        console.error('Error in deleteConsumable:', error);
        throw error;
    }
};

// Consumable Actions
export const addMovement = async (consumableId, data) => {
    const response = await api.post(`/api/inventory/consumables/${consumableId}/add_movement/`, data);
    return response.data;
};

// Inventory Movements
export const getMovements = async () => {
    const response = await api.get('/api/inventory/movements/');
    return response.data;
};

export const getMovement = async (id) => {
    const response = await api.get(`/api/inventory/movements/${id}/`);
    return response.data;
};

export const createMovement = async (data) => {
    const response = await api.post('/api/inventory/movements/', data);
    return response.data;
};

export const updateMovement = async (id, data) => {
    const response = await api.put(`/api/inventory/movements/${id}/`, data);
    return response.data;
};

export const deleteMovement = async (id) => {
    await api.delete(`/api/inventory/movements/${id}/`);
};

export const addInventoryMovement = async (consumableId, data) => {
    const response = await api.post(`/api/inventory/consumables/${consumableId}/add_movement/`, data);
    return response.data;
};

// Units of Measure
export const getUnitsOfMeasure = async () => {
    const response = await api.get('/api/inventory/units/');
    return normalizeList(response.data);
};

export const getUnitOfMeasure = async (id) => {
    const response = await api.get(`/api/inventory/units/${id}/`);
    return response.data;
};

export const createUnitOfMeasure = async (data) => {
    const response = await api.post('/api/inventory/units/', data);
    return response.data;
};

export const updateUnitOfMeasure = async (id, data) => {
    const response = await api.put(`/api/inventory/units/${id}/`, data);
    return response.data;
};

export const deleteUnitOfMeasure = async (id) => {
    await api.delete(`/api/inventory/units/${id}/`);
};

// Inventory Movements
export const getInventoryMovements = async () => {
    try {
        const response = await api.get('/api/inventory/movements/');
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error in getInventoryMovements:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        return [];
    }
};

export const getInventoryMovement = async (id) => {
    const response = await api.get(`/api/inventory/movements/${id}/`);
    return response.data;
};

export const createInventoryMovement = async (data) => {
    try {
        const response = await api.post('/api/inventory/movements/', data);
        return response.data;
    } catch (error) {
        console.error('Error in createInventoryMovement:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const updateInventoryMovement = async (id, data) => {
    const response = await api.put(`/api/inventory/movements/${id}/`, data);
    return response.data;
};

export const deleteInventoryMovement = async (id) => {
    await api.delete(`/api/inventory/movements/${id}/`);
};

// Locations
export const getLocations = async () => {
    try {
        const response = await api.get('/api/master-data/locations/');
        return normalizeList(response.data);
    } catch (error) {
        console.error('Error in getLocations:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        return [];
    }
};

export const createLocation = (data) => api.post('/api/master-data/locations/', data);
export const updateLocation = (id, data) => api.put(`/api/master-data/locations/${id}/`, data);
export const deleteLocation = (id) => api.delete(`/api/master-data/locations/${id}/`);

// Suppliers
export const getSuppliers = async () => {
    const response = await api.get('/api/master-data/suppliers/');
    return normalizeList(response.data);
};
export const createSupplier = (data) => api.post('/api/master-data/suppliers/', data);
export const updateSupplier = (id, data) => api.put(`/api/master-data/suppliers/${id}/`, data);
export const deleteSupplier = (id) => api.delete(`/api/master-data/suppliers/${id}/`);